'use client';

import { CACHE_KEY } from '@constants/cacheKey';

import {
  requestCategoryMolocoAdLpProductList,
  requestCategoryMolocoRecommendProductList,
  requestCategoryMolocoSimilarProductList,
  type CategoryMolocoRecommendResult,
  type CategoryMolocoSimilarAd,
} from '@apis/display/categoryMolocoLp';
import useGlobalState from '@hooks/useGlobalState';
import { useWckQuery } from '@hooks/wckQuery/useWckQuery';
import { getCustomAdAgreeYn } from '@wconcept/wconcept-common/utils/cookie';
import type { LpProductListCols } from '@wconcept/wconcept-common/utils/lpProductListCols';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

export function useCategoryMolocoController({
  mediumCd,
  largeCategory,
  middleCategory,
  smallCategory,
  categoryName,
  enabledLpAds,
  mobileColumnCount,
  similarItemCd,
  enabledSimilar,
  recommendItems,
  enabledRecommend,
}: {
  mediumCd: string;
  largeCategory: string;
  middleCategory: string;
  smallCategory?: string;
  categoryName: string;
  enabledLpAds: boolean;
  mobileColumnCount: LpProductListCols;
  similarItemCd?: string;
  enabledSimilar?: boolean;
  recommendItems?: string[];
  enabledRecommend?: boolean;
}) {
  const { isMobile } = useGlobalState();
  const pathName = usePathname();
  const [adAgreeYn, setAdAgreeYn] = useState('Y');

  useEffect(() => {
    const fetchAdAgreeYn = async () => {
      const result = await getCustomAdAgreeYn();
      setAdAgreeYn(result || 'Y');
    };
    fetchAdAgreeYn();
  }, []);

  const { data: categoryMolocoAdLpProductResult } = useWckQuery({
    queryKey: [
      CACHE_KEY.CATEGORY.CATEGORY_MOLOCO_AD_LP_PRODUCTS,
      pathName,
      adAgreeYn,
      mediumCd,
      largeCategory,
      middleCategory,
      mobileColumnCount,
    ],
    queryFn: async () => {
      if (adAgreeYn !== 'Y') {
        return null;
      }
      return requestCategoryMolocoAdLpProductList({
        pageId: pathName,
        categoryName,
        mediumCd,
        largeCategory,
        middleCategory,
      });
    },
    enabled: adAgreeYn === 'Y' && !!isMobile && enabledLpAds,
  });

  const { data: categoryMolocoSimilarAds, isFetched: isSimilarFetched } =
    useWckQuery<CategoryMolocoSimilarAd[]>({
      queryKey: [
        CACHE_KEY.CATEGORY.CATEGORY_MOLOCO_SIMILAR,
        pathName,
        adAgreeYn,
        similarItemCd,
      ],
      queryFn: async () => {
        if (adAgreeYn !== 'Y' || !similarItemCd) {
          return [];
        }
        return requestCategoryMolocoSimilarProductList({
          pageId: pathName,
          itemCd: similarItemCd,
          mediumCd,
          largeCategory,
          middleCategory,
        }).catch(() => []);
      },
      enabled:
        adAgreeYn === 'Y' && !!isMobile && !!similarItemCd && !!enabledSimilar,
    });

  const { data: categoryMolocoRecommendResult, isPending: isRecommendPending } =
    useWckQuery<CategoryMolocoRecommendResult | null>({
      queryKey: [
        CACHE_KEY.CATEGORY.CATEGORY_MOLOCO_RECOMMEND,
        pathName,
        adAgreeYn,
        smallCategory ?? '',
      ],
      queryFn: async () =>
        requestCategoryMolocoRecommendProductList({
          pageId: pathName,
          recommendItems: recommendItems ?? [],
          mediumCd,
          largeCategory,
          middleCategory,
          smallCategory: smallCategory ?? '',
          adAllowed: adAgreeYn === 'Y',
        }).catch(() => null),
      enabled: !!isMobile && !!enabledRecommend,
    });

  return {
    categoryMolocoAdLpProductResult: enabledLpAds
      ? categoryMolocoAdLpProductResult
      : undefined,
    categoryMolocoSimilarAds: categoryMolocoSimilarAds ?? undefined,
    /** 현재 similarItemCd 의 응답이 도착했는지 (빈 결과 포함) */
    isSimilarFetched,
    categoryMolocoRecommendResult: categoryMolocoRecommendResult ?? undefined,
    isRecommendPending: !!isMobile && !!enabledRecommend && isRecommendPending,
  };
}
